// Zones within e-paper countries. Managed from the E-Paper Settings screen
// and loaded from the backend by loadEpaperConfig() (config/epaperConfig.ts).
// The UAE list below is only the fallback before that first load.
export type Zone = {
  id?: number;
  name: string;
  code: string;
  country: string; // ISO2
  isActive: boolean;
  sortOrder: number;
};

// Shape returned by GET epaper-settings/config and /zones.
export type ZoneDto = {
  id: number;
  name: string;
  code: string;
  country_id: number | null;
  country_code: string | null;
  country_name: string | null;
  is_active: boolean;
  sort_order: number;
};

const FALLBACK_ZONE_CODE_MAP: Record<string, string> = {
  "Dubai": "DXB",
  "Abu Dhabi": "AUH",
  "Sharjah": "SHJ",
  "Ajman": "AJM",
  "UAQ (Umm Al Quwain)": "UAQ",
  "RAK (Ras Al Khaimah)": "RAK",
  "Fujairah": "FUJ",
  "Al Ain": "AIN",
  "Dhaid": "DHD",
  "Khorfakkan": "KHF",
  "Kalba": "KLB",
  "Dibba": "DIB",
  "Masafi": "MSF",
  "Madam": "MDM",
};

// Live data, mutated in place by setZones() so every importer sees reloads.
export const ZONES: Zone[] = Object.entries(FALLBACK_ZONE_CODE_MAP).map(
  ([name, code], i) => ({ name, code, country: "AE", isActive: true, sortOrder: i + 1 })
);
// Zone name → code, every zone (active and inactive).
export const ZONE_CODE_MAP: Record<string, string> = { ...FALLBACK_ZONE_CODE_MAP };
// Names of all active zones (any country).
export const zones: string[] = Object.keys(FALLBACK_ZONE_CODE_MAP);

export const setZones = (rows: ZoneDto[]) => {
  if (!Array.isArray(rows)) return;
  const mapped: Zone[] = rows
    .filter((z) => z.country_code)
    .map((z) => ({
      id: z.id,
      name: z.name,
      code: String(z.code).toUpperCase(),
      country: String(z.country_code).toUpperCase(),
      isActive: z.is_active !== false,
      sortOrder: Number(z.sort_order ?? 0),
    }));
  ZONES.splice(0, ZONES.length, ...mapped);
  Object.keys(ZONE_CODE_MAP).forEach((k) => delete ZONE_CODE_MAP[k]);
  mapped.forEach((z) => {
    if (!(z.name in ZONE_CODE_MAP)) ZONE_CODE_MAP[z.name] = z.code;
  });
  zones.splice(0, zones.length, ...mapped.filter((z) => z.isActive).map((z) => z.name));
};

export const zonesForCountry = (
  countryCode: string | null | undefined,
  { activeOnly = true }: { activeOnly?: boolean } = {}
): Zone[] =>
  ZONES.filter((z) => z.country === countryCode && (!activeOnly || z.isActive));

// A country uses a zone list only while it has an active zone; otherwise the
// country itself is stored as the zone (same rule as the backend).
export const countryHasZones = (countryCode: string | null | undefined): boolean =>
  zonesForCountry(countryCode).length > 0;

// Indian districts
export const DISTRICT_CODE_MAP: Record<string, string> = {
  "Thiruvananthapuram": "TVM",
  "Kollam": "KLM",
  "Pathanamthitta": "PTA",
  "Alappuzha": "ALP",
  "Kottayam": "KTM",
  "Idukki": "IDK",
  "Ernakulam": "EKM",
  "Thrissur": "TSR",
  "Palakkad": "PKD",
  "Malappuram": "MLP",
  "Kozhikode": "KZH",
  "Wayanad": "WYD",
  "Kannur": "KNR",
  "Kasaragod": "KSD",
  "Karnataka": "KAR",
  "Others": "OTH",
};

// Combined lookup (zone takes priority). Built from the fallback zones.
export const ALL_CODE_MAP: Record<string, string> = {
  ...DISTRICT_CODE_MAP,
  ...ZONE_CODE_MAP,
};

export const districts = Object.keys(DISTRICT_CODE_MAP);
